import React from 'react';
import { useTranslation } from '../context/TranslationContext';

interface RequestDetailsTabProps {
  request: any;
  formatDate: (dateString: string) => string;
}

const RequestDetailsTab: React.FC<RequestDetailsTabProps> = ({ request, formatDate }) => {
  const { t } = useTranslation();

  if (!request) {
    return (
      <div className="main-content">
        <div className="loading-state">
          <p>{t('common.loading_request_details')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      {/* Product Information Section */}
      <div className="content-section">
        <h2 className="section-title section-title-black">
          {t('common.product_information')}
        </h2>
        <div className="info-grid">
          <div className="info-card">
            <h3 className="section-title-black">{t('common.basic_information')}</h3>
            <div className="info-item">
              <label>{t('common.product_name')}:</label>
              <span>{request.product_name}</span>
            </div>
            {request.product_brand && (
              <div className="info-item">
                <label>{t('common.product_brand')}:</label>
                <span>{request.product_brand}</span>
              </div>
            )}
            {request.product_description && (
              <div className="info-item">
                <label>{t('common.product_description')}:</label>
                <span>{request.product_description}</span>
              </div>
            )}
            <div className="info-item">
              <label>{t('common.category')}:</label>
              <span>{request.product_category || 'N/A'}</span>
            </div>
          </div>

          <div className="info-card">
            <h3 className="section-title-black">{t('common.specifications')}</h3>
            <div className="info-item">
              <label>{t('common.quantity')}:</label>
              <span>{request.quantity} {request.unit}</span>
            </div>
            <div className="info-item">
              <label>{t('common.budget')}:</label>
              <span>{request.budget} {request.currency}</span>
            </div>
            <div className="info-item">
              <label>{t('common.delivery_time')}:</label>
              <span>{formatDate(request.delivery_time)}</span>
            </div>
            <div className="info-item">
              <label>{t('common.urgency')}:</label>
              <span className={`urgency-${request.urgency?.toLowerCase()}`}>
                {request.urgency || 'Normal'}
              </span>
            </div>
          </div>

          <div className="info-card">
            <h3 className="section-title-black">{t('common.location_preferences')}</h3>
            <div className="info-item">
              <label>{t('common.region')}:</label>
              <span>{request.region?.join(', ') || 'Any'}</span>
            </div>
            {request.address && (
              <div className="info-item">
                <label>{t('common.delivery_address')}:</label>
                <span>{request.address}</span>
              </div>
            )}
          </div>

          {request.current_supplier && (
            <div className="info-card">
              <h3>{t('common.current_supplier')}</h3>
              <div className="info-item">
                <label>{t('common.supplier_name')}:</label>
                <span>{request.current_supplier}</span>
              </div>
              {request.current_price && (
                <div className="info-item">
                  <label>{t('common.current_price')}:</label>
                  <span>{request.current_price} {request.currency}</span>
                </div>
              )}
            </div>
          )}

          {request.additional_specifications && request.additional_specifications.length > 0 && (
            <div className="info-card full-width">
              <h3>{t('common.additional_specifications')}</h3>
              <div className="specifications-list">
                {request.additional_specifications.map((spec: string, index: number) => (
                  <div key={index} className="specification-item">
                    <span className="spec-dot"></span>
                    {spec}
                  </div>
                ))}
              </div>
            </div>
          )}

          {request.additional_notes && (
            <div className="info-card full-width">
              <h3>{t('common.additional_notes')}</h3>
              <p className="notes-text">{request.additional_notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Requester Information Section */}
      <div className="content-section">
        <h2 className="section-title section-title-black">
          {t('common.requester_information')}
        </h2>
        <div className="info-grid">
          <div className="info-card">
            <h3>{t('common.requester_details')}</h3>
            <div className="info-item">
              <label>{t('common.name')}:</label>
              <span>{request.user_name}</span>
            </div>
            <div className="info-item">
              <label>{t('common.email')}:</label>
              <span>{request.user_email}</span>
            </div>
            <div className="info-item">
              <label>{t('common.department')}:</label>
              <span>{request.user_department || 'N/A'}</span>
            </div>
          </div>

          <div className="info-card">
            <h3>{t('common.company_information')}</h3>
            <div className="info-item">
              <label>{t('common.company_name')}:</label>
              <span>{request.company_name || 'Independent User'}</span>
            </div>
            {request.company_email && (
              <div className="info-item">
                <label>{t('common.company_email')}:</label>
                <span>{request.company_email}</span>
              </div>
            )}
          </div>

          <div className="info-card">
            <h3>{t('common.request_metadata')}</h3>
            <div className="info-item">
              <label>{t('common.created_at')}:</label>
              <span>{formatDate(request.created_at)}</span>
            </div>
            <div className="info-item">
              <label>{t('common.updated_at')}:</label>
              <span>{formatDate(request.updated_at)}</span>
            </div>
            {request.edited_by && (
              <div className="info-item">
                <label>{t('common.last_edited_by')}:</label>
                <span>{request.edited_by}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Request Timeline Section */}
      <div className="content-section">
        <h2 className="section-title section-title-black">
          {t('common.request_timeline')}
        </h2>
        <div className="timeline-container">
          {request.phase_history && request.phase_history.length > 0 ? (
            request.phase_history.map((phase: any, index: number) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <div className="timeline-header">
                    <h4 className="section-title-black">{phase.phase}</h4>
                    <span className="timeline-date" style={{color: '#000000', fontSize: '0.9rem'}}>
                      {formatDate(phase.timestamp)}
                    </span>
                  </div>
                  {phase.trigger && (
                    <p className="timeline-trigger" style={{color: '#000000', fontSize: '0.9rem', margin: '0.25rem 0 0 0'}}>
                      Trigger: {phase.trigger}
                    </p>
                  )}
                  {phase.supplier_index !== undefined && (
                    <p className="timeline-supplier" style={{color: '#000000', fontSize: '0.9rem', margin: '0.25rem 0 0 0'}}>
                      Supplier Index: {phase.supplier_index}
                    </p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="no-timeline">
              <p>{t('common.no_timeline_data')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestDetailsTab;
